<template>
  <div
    class="fixed bottom-0 left-0 right-0 z-30 bg-base-100 border-t border-base-300"
    style="box-shadow: 0 -1px 6px rgba(0, 0, 0, 0.08)"
  >
    <!-- Progress bar (always visible while work is in flight) -->
    <div class="h-1 w-full bg-base-200" v-if="showProgress">
      <div
        class="h-full bg-primary"
        :style="`width: ${progressPercent}%`"
      ></div>
    </div>

    <!-- Collapsed status row — always visible, click to expand -->
    <div
      role="button"
      tabindex="0"
      class="flex items-center gap-3 w-full px-4 py-1.5 text-left hover:bg-base-200/50 transition-colors cursor-pointer"
      @click="expanded = !expanded"
      @keydown.enter.prevent="expanded = !expanded"
      @keydown.space.prevent="expanded = !expanded"
    >
      <Icon
        :icon="expanded ? 'feather:chevron-down' : 'feather:chevron-up'"
        class="w-4 h-4 shrink-0 text-base-content/60"
      ></Icon>
      <span class="text-sm text-base-content/70 truncate flex-1">
        {{ latestMessage || "Ready" }}
      </span>
      <span
        v-if="showProgress"
        class="text-xs text-base-content/50 shrink-0 tabular-nums"
      >
        {{ Math.round(progressPercent) }}%
      </span>
      <!-- Badges jump straight to the filtered issue list -->
      <button
        v-if="errorCount > 0"
        type="button"
        :title="`Show ${errorCount} error${errorCount === 1 ? '' : 's'}`"
        class="shrink-0 inline-flex items-center gap-1 text-xs text-error border border-error/40 bg-error/10 hover:bg-error/20 rounded px-1.5 py-0.5"
        @click.stop="showIssues"
      >
        <Icon icon="feather:alert-octagon" class="w-3 h-3"></Icon>
        {{ errorCount }}
      </button>
      <button
        v-if="warnCount > 0"
        type="button"
        :title="`Show ${warnCount} warning${warnCount === 1 ? '' : 's'}`"
        class="shrink-0 inline-flex items-center gap-1 text-xs text-warning border border-warning/40 bg-warning/10 hover:bg-warning/20 rounded px-1.5 py-0.5"
        @click.stop="showIssues"
      >
        <Icon icon="feather:alert-triangle" class="w-3 h-3"></Icon>
        {{ warnCount }}
      </button>
      <span class="text-xs text-base-content/40 shrink-0">Logs</span>
    </div>

    <!-- Expanded panel (kept in DOM via v-show so diagnostics monitor persists) -->
    <div v-show="expanded" class="border-t border-base-300">
      <div class="flex items-center gap-1 px-3 pt-2">
        <button
          type="button"
          class="px-3 py-1 text-xs rounded-t transition-colors"
          :class="
            subtab === 'logs'
              ? 'bg-base-200 text-base-content font-medium'
              : 'text-base-content/50 hover:text-base-content'
          "
          @click="subtab = 'logs'"
        >
          Logs
        </button>
        <button
          type="button"
          class="px-3 py-1 text-xs rounded-t transition-colors"
          :class="
            subtab === 'diagnostics'
              ? 'bg-base-200 text-base-content font-medium'
              : 'text-base-content/50 hover:text-base-content'
          "
          @click="subtab = 'diagnostics'"
        >
          Diagnostics
        </button>
      </div>

      <!-- Logs -->
      <div v-show="subtab === 'logs'" class="flex flex-col" style="height: 50vh">
        <!-- Filter: all messages vs. errors & warnings only -->
        <div
          class="flex items-center gap-1 px-4 pt-2 pb-1 border-b border-base-200"
        >
          <button
            type="button"
            class="px-2 py-0.5 text-xs rounded transition-colors"
            :class="
              logFilter === 'all'
                ? 'bg-base-300 text-base-content font-medium'
                : 'text-base-content/50 hover:text-base-content'
            "
            @click="logFilter = 'all'"
          >
            All
          </button>
          <button
            type="button"
            class="px-2 py-0.5 text-xs rounded transition-colors inline-flex items-center gap-1"
            :class="
              logFilter === 'issues'
                ? 'bg-base-300 text-base-content font-medium'
                : 'text-base-content/50 hover:text-base-content'
            "
            @click="logFilter = 'issues'"
          >
            Errors &amp; warnings
            <span
              v-if="issueCount > 0"
              class="tabular-nums"
              :class="errorCount > 0 ? 'text-error' : 'text-warning'"
              >{{ issueCount }}</span
            >
          </button>
        </div>

        <div
          class="px-4 py-3 overflow-y-auto text-sm flex-1"
          style="overscroll-behavior: contain"
        >
          <div v-if="displayedLogs.length === 0" class="text-base-content/40">
            {{
              logFilter === "issues"
                ? "No errors or warnings."
                : "No log messages yet."
            }}
          </div>
          <div v-if="droppedCount > 0" class="text-base-content/40 pb-1">
            Showing the most recent {{ displayedLogs.length }} messages
            ({{ droppedCount.toLocaleString() }} older ones not shown).
          </div>
          <div
            v-for="(log, index) in displayedLogs"
            :key="index"
            class="py-0.5 border-b border-base-200 last:border-0"
          >
            <span class="font-bold" :class="logColor(log.level)"
              >{{ log.level }}:</span
            >
            {{ log.message }}
            <div v-if="log.sublogs" class="ml-4">
              <a
                href="#"
                class="text-xs"
                @click.prevent="
                  openSublogsFor = openSublogsFor === log ? null : log
                "
              >
                {{ log.sublogs.length }} additional log{{
                  log.sublogs.length === 1 ? "" : "s"
                }}
              </a>
              <div
                v-if="openSublogsFor === log"
                class="bg-base-200 p-2 border border-base-300 text-xs mt-1"
              >
                <div v-for="(sublog, si) in log.sublogs" :key="si">
                  <span class="font-bold" :class="logColor(sublog.level)"
                    >{{ sublog.level }}:</span
                  >
                  {{ sublog.message }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Diagnostics (memory / OPFS / cache) -->
      <div
        v-show="subtab === 'diagnostics'"
        class="px-4 py-3 text-xs"
        style="height: 50vh; overflow-y: auto; overscroll-behavior: contain"
      >
        <div
          ref="memoryDiv"
          class="font-mono leading-relaxed text-base-content/70"
        >
          Loading diagnostics…
        </div>
        <div class="mt-2">
          <a href="#" @click.prevent="copyDiagnosticReport">
            {{
              diagnosticCopyState === "copied"
                ? "Copied to clipboard"
                : "Copy diagnostic report"
            }}
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, shallowRef } from "vue";
import { Icon } from "@iconify/vue";
import {
  logs,
  problemLogs,
  latestMessage,
  progressBar,
  errorCount,
  warnCount,
  droppedLogCount,
  droppedProblemCount,
} from "./logger";
import type { Log, LogLevel } from "./logger";
import monitorMemory from "./monitorMemory";
import { logSessionReport } from "./session-stats";

const expanded = ref(false);
const subtab = ref<"logs" | "diagnostics">("logs");
const logFilter = ref<"all" | "issues">("all");
// shallowRef so identity comparison against the (plain) log objects holds.
const openSublogsFor = shallowRef<Log | null>(null);

const issueCount = computed(() => errorCount.value + warnCount.value);
const displayedLogs = computed(() =>
  logFilter.value === "issues" ? problemLogs.value : logs.value
);
const droppedCount = computed(() =>
  logFilter.value === "issues" ? droppedProblemCount.value : droppedLogCount.value
);

function showIssues() {
  expanded.value = true;
  subtab.value = "logs";
  logFilter.value = "issues";
}

const progressPercent = computed(() => {
  if (progressBar.value.numTotal === 0) return 0;
  return (progressBar.value.numDone / progressBar.value.numTotal) * 100;
});
const showProgress = computed(
  () => progressPercent.value > 0 && progressPercent.value < 100
);

function logColor(level: LogLevel) {
  if (level === "error") return "text-error";
  if (level === "warn") return "text-warning";
  return "text-base-content/70";
}

const memoryDiv = ref<HTMLElement | null>(null);
const diagnosticCopyState = ref<"idle" | "copied">("idle");

async function copyDiagnosticReport() {
  await logSessionReport();
  diagnosticCopyState.value = "copied";
  setTimeout(() => {
    diagnosticCopyState.value = "idle";
  }, 2500);
}

onMounted(() => {
  // The element is always present (v-show keeps it mounted), so the monitor
  // can start once and keep updating its target node.
  if (memoryDiv.value) monitorMemory(memoryDiv.value);
});
</script>
